import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { ICliente, IClienteTable, ILimitacaoConsumoTable, IPerfil, IProdutoLimitacaoConsumoDetails, IUsuario } from "../../models"
import { IFormError, IFormProps } from "../utils"

export type IModoTravaCliente = 'produto' | 'tipoConsumo'

interface ICreateLimitacaoConsumoClienteFormFields {
    IdCliente: number
    Quantidade: number
    IdProduto: number
    IdTipoConsumo: number[]
    IdUsuario: number
    ModoTravaCliente: IModoTravaCliente
    IsGrupo: boolean
}

interface IUpdateLimitacaoConsumoClienteFormFields {
    IdLimitacaoConsumoCliente: number
    Quantidade: number
    Ativo: boolean
    IdUsuario: number
    IdCliente: number
    IdGrupoTrava?: number
}

const createLimitacaoConsumoClienteFormFieldsInitialState: ICreateLimitacaoConsumoClienteFormFields = {
    IdCliente: 0,
    Quantidade: 0,
    IdProduto: 0,
    IdTipoConsumo: [],
    IdUsuario: 0,
    ModoTravaCliente: 'produto',
    IsGrupo: false
}

const updateLimitacaoConsumoClienteFormFieldsInitialState: IUpdateLimitacaoConsumoClienteFormFields = {
    IdLimitacaoConsumoCliente: 0,
    Quantidade: 0,
    Ativo: false,
    IdUsuario: 0,
    IdCliente: 0,
    IdGrupoTrava: undefined
}

// Trava de Consumo - Usuário
interface ICreateLimitacaoConsumoUsuarioFormFields {
    IdUsuario: number
    Quantidade: number
    IdProduto: number
    IdTipoConsumo: number[]
    IsGrupo: boolean
}

interface IUpdateLimitacaoConsumoUsuarioFormFields {
    IdLimitacaoConsumoUsuario: number
    Quantidade: number
    Ativo: boolean
    IdUsuario: number
    IdGrupoTrava?: number
}

const createLimitacaoConsumoUsuarioFormFieldsInitialState: ICreateLimitacaoConsumoUsuarioFormFields = {
    IdUsuario: 0,
    Quantidade: 0,
    IdProduto: 0,
    IdTipoConsumo: [],
    IsGrupo: false
}

const updateLimitacaoConsumoUsuarioFormFieldsInitialState: IUpdateLimitacaoConsumoUsuarioFormFields = {
    IdLimitacaoConsumoUsuario: 0,
    Quantidade: 0,
    Ativo: false,
    IdUsuario: 0,
    IdGrupoTrava: undefined
}

// Trava de Consumo - Perfil
interface ICreateLimitacaoConsumoPerfilFormFields {
    IdPerfil: number
    Quantidade: number
    IdProduto: number
    IdTipoConsumo: number[]
    IdUsuario: number
    IsGrupo: boolean
}

interface IUpdateLimitacaoConsumoPerfilFormFields {
    IdLimitacaoConsumoPerfil: number
    IdPerfil: number
    Quantidade: number
    Ativo: boolean
    IdUsuario: number
    IdGrupoTrava?: number
}

const createLimitacaoConsumoPerfilFormFieldsInitialState: ICreateLimitacaoConsumoPerfilFormFields = {
    IdPerfil: 0,
    Quantidade: 0,
    IdProduto: 0,
    IdTipoConsumo: [],
    IdUsuario: 0,
    IsGrupo: false
}

const updateLimitacaoConsumoPerfilFormFieldsInitialState: IUpdateLimitacaoConsumoPerfilFormFields = {
    IdLimitacaoConsumoPerfil: 0,
    IdPerfil: 0,
    Quantidade: 0,
    Ativo: false,
    IdUsuario: 0,
    IdGrupoTrava: undefined
}

interface ILimitacaoConsumoState {
    clientes: IClienteTable[]
    limitacaoConsumoPagination: {
        page: number
        limit: number
    }
    clienteSelecionado?: ICliente
    usuarios: Omit<IUsuario, "Senha">[]
    usuarioSelecionado?: Omit<IUsuario, "Senha">
    perfis: IPerfil[]
    perfilSelecionado?: IPerfil
    createLimitacaoConsumoClienteForm: IFormProps<ICreateLimitacaoConsumoClienteFormFields>
    createLimitacaoConsumoClienteOpenForm: boolean
    updateLimitacaoConsumoClienteForm: IFormProps<IUpdateLimitacaoConsumoClienteFormFields>
    updateLimitacaoConsumoClienteOpenForm: boolean
    createLimitacaoConsumoUsuarioForm: IFormProps<ICreateLimitacaoConsumoUsuarioFormFields>
    createLimitacaoConsumoUsuarioOpenForm: boolean
    updateLimitacaoConsumoUsuarioForm: IFormProps<IUpdateLimitacaoConsumoUsuarioFormFields>
    updateLimitacaoConsumoUsuarioOpenForm: boolean
    createLimitacaoConsumoPerfilForm: IFormProps<ICreateLimitacaoConsumoPerfilFormFields>
    createLimitacaoConsumoPerfilOpenForm: boolean
    updateLimitacaoConsumoPerfilForm: IFormProps<IUpdateLimitacaoConsumoPerfilFormFields>
    updateLimitacaoConsumoPerfilOpenForm: boolean
    limitacaoConsumoList: ILimitacaoConsumoTable[]
    limitacaoConsumoTotal: number
    produtosList: IProdutoLimitacaoConsumoDetails[]
}

const initialState: ILimitacaoConsumoState = {
    clientes: [],
    limitacaoConsumoPagination: {
        page: 0,
        limit: 0
    },
    usuarios: [],
    perfis: [],
    createLimitacaoConsumoClienteForm: {
        errors: [],
        fields: createLimitacaoConsumoClienteFormFieldsInitialState,
        touched: false,
        delivered: false
    },
    createLimitacaoConsumoClienteOpenForm: false,
    updateLimitacaoConsumoClienteForm: {
        errors: [],
        fields: updateLimitacaoConsumoClienteFormFieldsInitialState,
        touched: false,
        delivered: false
    },
    updateLimitacaoConsumoClienteOpenForm: false,
    createLimitacaoConsumoUsuarioForm: {
        errors: [],
        fields: createLimitacaoConsumoUsuarioFormFieldsInitialState,
        touched: false,
        delivered: false
    },
    createLimitacaoConsumoUsuarioOpenForm: false,
    updateLimitacaoConsumoUsuarioForm: {
        errors: [],
        fields: updateLimitacaoConsumoUsuarioFormFieldsInitialState,
        touched: false,
        delivered: false
    },
    updateLimitacaoConsumoUsuarioOpenForm: false,
    createLimitacaoConsumoPerfilForm: {
        errors: [],
        fields: createLimitacaoConsumoPerfilFormFieldsInitialState,
        touched: false,
        delivered: false
    },
    createLimitacaoConsumoPerfilOpenForm: false,
    updateLimitacaoConsumoPerfilForm: {
        errors: [],
        fields: updateLimitacaoConsumoPerfilFormFieldsInitialState,
        touched: false,
        delivered: false
    },
    updateLimitacaoConsumoPerfilOpenForm: false,
    limitacaoConsumoList: [],
    limitacaoConsumoTotal: 0,
    produtosList: []
}

export const validateCreateLimitacaoConsumoClienteForm = (fields: ICreateLimitacaoConsumoClienteFormFields): IFormError<keyof ICreateLimitacaoConsumoClienteFormFields>[] => {
    const errors: IFormError<keyof ICreateLimitacaoConsumoClienteFormFields>[] = []

    if (!fields.IdCliente || fields.IdCliente === 0) {
        errors.push({
            key: 'IdCliente',
            message: 'Selecione um cliente'
        })
    } else if (!fields.IdUsuario || fields.IdUsuario === 0) {
        errors.push({
            key: 'IdUsuario',
            message: 'Usuário da sessão não identificado'
        })
    } else if (!fields.Quantidade || fields.Quantidade <= 0) {
        errors.push({
            key: 'Quantidade',
            message: 'A quantidade deve ser maior ou igual a 1'
        })
    } else if (fields.ModoTravaCliente === 'produto') {
        if (!fields.IdProduto || fields.IdProduto === 0) {
            errors.push({
                key: 'IdProduto',
                message: 'Selecione um produto'
            })
        }
    } else {
        if (fields.IsGrupo) {
            if (!fields.IdProduto || fields.IdProduto === 0) {
                errors.push({
                    key: 'IdProduto',
                    message: 'Selecione um produto'
                })
            }
        }
        if (!fields.IdTipoConsumo || fields.IdTipoConsumo.length === 0) {
            errors.push({
                key: 'IdTipoConsumo',
                message: 'Selecione pelo menos um tipo de consumo'
            })
        }
    }

    return errors
}

export const validateUpdateLimitacaoConsumoClienteForm = (fields: IUpdateLimitacaoConsumoClienteFormFields): IFormError<keyof IUpdateLimitacaoConsumoClienteFormFields>[] => {
    const errors: IFormError<keyof IUpdateLimitacaoConsumoClienteFormFields>[] = []

    if (!fields.IdLimitacaoConsumoCliente || fields.IdLimitacaoConsumoCliente === 0) {
        errors.push({
            key: 'IdLimitacaoConsumoCliente',
            message: 'Selecione uma trava para o cliente antes de atualizar'
        })
    } else if (!fields.Quantidade || fields.Quantidade <= 0) {
        errors.push({
            key: 'Quantidade',
            message: 'A quantidade deve ser maior ou igual a 1'
        })
    }

    return errors
}

export const validateCreateLimitacaoConsumoUsuarioForm = (fields: ICreateLimitacaoConsumoUsuarioFormFields): IFormError<keyof ICreateLimitacaoConsumoUsuarioFormFields>[] => {
    const errors: IFormError<keyof ICreateLimitacaoConsumoUsuarioFormFields>[] = []

    if (!fields.IdUsuario || fields.IdUsuario === 0) {
        errors.push({
            key: 'IdUsuario',
            message: 'Selecione um usuário'
        })
    } else if (!fields.Quantidade || fields.Quantidade <= 0) {
        errors.push({
            key: 'Quantidade',
            message: 'A quantidade deve ser maior ou igual a 1'
        })
    } else {
        const hasProduto = Boolean(fields.IdProduto && fields.IdProduto > 0)
        const hasTipos = Array.isArray(fields.IdTipoConsumo) && fields.IdTipoConsumo.length > 0

        if (fields.IsGrupo) {
            if (!hasProduto) {
                errors.push({
                    key: 'IdProduto',
                    message: 'Selecione um produto'
                })
            }
            if (!hasTipos) {
                errors.push({
                    key: 'IdTipoConsumo',
                    message: 'Selecione pelo menos um tipo de consumo'
                })
            }
        } else {
            if (!hasProduto && !hasTipos) {
                errors.push({
                    key: 'IdProduto',
                    message: 'Selecione um produto ou uma variável'
                })
            }
        }
    }

    return errors
}

export const validateUpdateLimitacaoConsumoUsuarioForm = (fields: IUpdateLimitacaoConsumoUsuarioFormFields): IFormError<keyof IUpdateLimitacaoConsumoUsuarioFormFields>[] => {
    const errors: IFormError<keyof IUpdateLimitacaoConsumoUsuarioFormFields>[] = []

    const isGrupo = fields.IdGrupoTrava != null && fields.IdGrupoTrava > 0
    const hasValidId = (fields.IdLimitacaoConsumoUsuario && fields.IdLimitacaoConsumoUsuario > 0) || isGrupo

    if (!hasValidId) {
        errors.push({
            key: 'IdLimitacaoConsumoUsuario',
            message: 'Selecione uma trava para o usuário antes de atualizar'
        })
    } else if (!fields.Quantidade || fields.Quantidade <= 0) {
        errors.push({
            key: 'Quantidade',
            message: 'A quantidade deve ser maior ou igual a 1'
        })
    }

    return errors
}

const limitacaoConsumoSlice = createSlice({
    name: 'LimitacaoConsumo',
    initialState,
    reducers: {
        setLimitacaoConsumoClientesState(state, action: PayloadAction<{
            clientes: IClienteTable[]
        }>) {
            state.clientes = action.payload.clientes
        },
        resetLimitacaoConsumoClientesState(state) {
            state.clientes = initialState.clientes
        },
        setLimitacaoConsumoPaginationState(state, action: PayloadAction<{
            page: number
            limit: number
        }>) {
            state.limitacaoConsumoPagination.page = action.payload.page
            state.limitacaoConsumoPagination.limit = action.payload.limit
        },
        setLimitacaoConsumoPageState(state, action: PayloadAction<number>) {
            state.limitacaoConsumoPagination.page = action.payload
        },
        setLimitacaoConsumoLimitState(state, action: PayloadAction<number>) {
            state.limitacaoConsumoPagination.limit = action.payload
        },
        resetLimitacaoConsumoPaginationState(state) {
            state.limitacaoConsumoPagination = initialState.limitacaoConsumoPagination
        },
        setLimitacaoConsumoUsuariosState(state, action: PayloadAction<Omit<IUsuario, "Senha">[]>) {
            state.usuarios = action.payload
        },
        resetLimitacaoConsumoUsuariosState(state) {
            state.usuarios = initialState.usuarios
        },
        setLimitacaoConsumoPerfisState(state, action: PayloadAction<IPerfil[]>) {
            state.perfis = action.payload
        },
        resetLimitacaoConsumoPerfisState(state) {
            state.perfis = initialState.perfis
        },
        setCreateLimitacaoConsumoClienteFormIdClienteState(state, action: PayloadAction<number>) {
            state.createLimitacaoConsumoClienteForm.fields.IdCliente = action.payload
        },
        setCreateLimitacaoConsumoClienteFormIdProdutoState(state, action: PayloadAction<number>) {
            state.createLimitacaoConsumoClienteForm.fields.IdProduto = action.payload
        },
        setCreateLimitacaoConsumoClienteFormModoTravaClienteState(state, action: PayloadAction<IModoTravaCliente>) {
            state.createLimitacaoConsumoClienteForm.fields.ModoTravaCliente = action.payload
            state.createLimitacaoConsumoClienteForm.fields.IsGrupo = false
            if (action.payload === 'produto') {
                state.createLimitacaoConsumoClienteForm.fields.IdTipoConsumo = []
            } else {
                state.createLimitacaoConsumoClienteForm.fields.IdProduto = 0
            }
        },
        setCreateLimitacaoConsumoClienteFormIsGrupoState(state, action: PayloadAction<boolean>) {
            state.createLimitacaoConsumoClienteForm.fields.IsGrupo = action.payload
        },

        setCreateLimitacaoConsumoClienteFormIdTipoConsumoState(state, action: PayloadAction<number[]>) {
            state.createLimitacaoConsumoClienteForm.fields.IdTipoConsumo = action.payload
        },
        setCreateLimitacaoConsumoClienteFormQuantidadeState(state, action: PayloadAction<number>) {
            state.createLimitacaoConsumoClienteForm.fields.Quantidade = action.payload
        },
        setCreateLimitacaoConsumoClienteFormIdUsuarioState(state, action: PayloadAction<number>) {
            state.createLimitacaoConsumoClienteForm.fields.IdUsuario = action.payload
        },
        setCreateLimitacaoConsumoClienteFormTouchedState(state, action: PayloadAction<boolean>) {
            state.createLimitacaoConsumoClienteForm.touched = action.payload
        },
        setCreateLimitacaoConsumoClienteFormErrorsState(state, action: PayloadAction<IFormError<keyof ICreateLimitacaoConsumoClienteFormFields>[]>) {
            state.createLimitacaoConsumoClienteForm.errors = action.payload
        },
        resetCreateLimitacaoConsumoClienteFormErrorsState(state) {
            state.createLimitacaoConsumoClienteForm.errors = initialState.createLimitacaoConsumoClienteForm.errors
        },
        setCreateLimitacaoConsumoClienteFormDeliveredState(state, action: PayloadAction<boolean>) {
            state.updateLimitacaoConsumoClienteForm.delivered = action.payload
        },
        resetCreateLimitacaoConsumoClienteFormState(state) {
            state.createLimitacaoConsumoClienteForm = initialState.createLimitacaoConsumoClienteForm
        },
        setUpdateLimitacaoConsumoClienteFormIdLimitacaoConsumoClienteState(state, action: PayloadAction<number>) {
            state.updateLimitacaoConsumoClienteForm.fields.IdLimitacaoConsumoCliente = action.payload
        },
        setUpdateLimitacaoConsumoClienteFormAtivoState(state, action: PayloadAction<boolean>) {
            state.updateLimitacaoConsumoClienteForm.fields.Ativo = action.payload
        },
        setUpdateLimitacaoConsumoClienteFormQuantidadeState(state, action: PayloadAction<number>) {
            state.updateLimitacaoConsumoClienteForm.fields.Quantidade = action.payload
        },
        setUpdateLimitacaoConsumoClienteFormTouchedState(state, action: PayloadAction<boolean>) {
            state.updateLimitacaoConsumoClienteForm.touched = action.payload
        },
        setUpdateLimitacaoConsumoClienteFormIdGrupoTravaState(state, action: PayloadAction<number | undefined>) {
            state.updateLimitacaoConsumoClienteForm.fields.IdGrupoTrava = action.payload
        },
        setUpdateLimitacaoConsumoClienteFormIdClienteState(state, action: PayloadAction<number>) {
            state.updateLimitacaoConsumoClienteForm.fields.IdCliente = action.payload
        },
        setUpdateLimitacaoConsumoClienteFormErrorsState(state, action: PayloadAction<IFormError<keyof IUpdateLimitacaoConsumoClienteFormFields>[]>) {
            state.updateLimitacaoConsumoClienteForm.errors = action.payload
        },
        setUpdateLimitacaoConsumoClienteFormDeliveredState(state, action: PayloadAction<boolean>) {
            state.updateLimitacaoConsumoClienteForm.delivered = action.payload
        },
        resetUpdateLimitacaoConsumoClienteFormState(state) {
            state.updateLimitacaoConsumoClienteForm = initialState.updateLimitacaoConsumoClienteForm
        },
        setCreateLimitacaoConsumoClienteOpenFormState(state, action: PayloadAction<boolean>) {
            state.createLimitacaoConsumoClienteOpenForm = action.payload
        },
        setUpdateLimitacaoConsumoClienteOpenFormState(state, action: PayloadAction<boolean>) {
            state.updateLimitacaoConsumoClienteOpenForm = action.payload
        },
        setCreateLimitacaoConsumoUsuarioFormIdUsuarioState(state, action: PayloadAction<number>) {
            state.createLimitacaoConsumoUsuarioForm.fields.IdUsuario = action.payload
        },
        setCreateLimitacaoConsumoUsuarioFormIdProdutoState(state, action: PayloadAction<number>) {
            state.createLimitacaoConsumoUsuarioForm.fields.IdProduto = action.payload
        },
        setCreateLimitacaoConsumoUsuarioFormIdTipoConsumoState(state, action: PayloadAction<number[]>) {
            state.createLimitacaoConsumoUsuarioForm.fields.IdTipoConsumo = action.payload
        },
        setCreateLimitacaoConsumoUsuarioFormIsGrupoState(state, action: PayloadAction<boolean>) {
            state.createLimitacaoConsumoUsuarioForm.fields.IsGrupo = action.payload
        },
        setCreateLimitacaoConsumoUsuarioFormQuantidadeState(state, action: PayloadAction<number>) {
            state.createLimitacaoConsumoUsuarioForm.fields.Quantidade = action.payload
        },
        setCreateLimitacaoConsumoUsuarioFormTouchedState(state, action: PayloadAction<boolean>) {
            state.createLimitacaoConsumoUsuarioForm.touched = action.payload
        },
        setCreateLimitacaoConsumoUsuarioFormErrorsState(state, action: PayloadAction<IFormError<keyof ICreateLimitacaoConsumoUsuarioFormFields>[]>) {
            state.createLimitacaoConsumoUsuarioForm.errors = action.payload
        },
        resetCreateLimitacaoConsumoUsuarioFormErrorsState(state) {
            state.createLimitacaoConsumoUsuarioForm.errors = initialState.createLimitacaoConsumoUsuarioForm.errors
        },
        setCreateLimitacaoConsumoUsuarioFormDeliveredState(state, action: PayloadAction<boolean>) {
            state.updateLimitacaoConsumoUsuarioForm.delivered = action.payload
        },
        resetCreateLimitacaoConsumoUsuarioFormState(state) {
            state.createLimitacaoConsumoUsuarioForm = initialState.createLimitacaoConsumoUsuarioForm
        },
        setUpdateLimitacaoConsumoUsuarioFormIdLimitacaoConsumoUsuarioState(state, action: PayloadAction<number>) {
            state.updateLimitacaoConsumoUsuarioForm.fields.IdLimitacaoConsumoUsuario = action.payload
        },
        setUpdateLimitacaoConsumoUsuarioFormIdUsuarioState(state, action: PayloadAction<number>) {
            state.updateLimitacaoConsumoUsuarioForm.fields.IdUsuario = action.payload
        },
        setUpdateLimitacaoConsumoUsuarioFormAtivoState(state, action: PayloadAction<boolean>) {
            state.updateLimitacaoConsumoUsuarioForm.fields.Ativo = action.payload
        },
        setUpdateLimitacaoConsumoUsuarioFormQuantidadeState(state, action: PayloadAction<number>) {
            state.updateLimitacaoConsumoUsuarioForm.fields.Quantidade = action.payload
        },
        setUpdateLimitacaoConsumoUsuarioFormIdGrupoTravaState(state, action: PayloadAction<number | undefined>) {
            state.updateLimitacaoConsumoUsuarioForm.fields.IdGrupoTrava = action.payload
        },
        setUpdateLimitacaoConsumoUsuarioFormTouchedState(state, action: PayloadAction<boolean>) {
            state.updateLimitacaoConsumoUsuarioForm.touched = action.payload
        },
        setUpdateLimitacaoConsumoUsuarioFormErrorsState(state, action: PayloadAction<IFormError<keyof IUpdateLimitacaoConsumoUsuarioFormFields>[]>) {
            state.updateLimitacaoConsumoUsuarioForm.errors = action.payload
        },
        setUpdateLimitacaoConsumoUsuarioFormDeliveredState(state, action: PayloadAction<boolean>) {
            state.updateLimitacaoConsumoUsuarioForm.delivered = action.payload
        },
        resetUpdateLimitacaoConsumoUsuarioFormState(state) {
            state.updateLimitacaoConsumoUsuarioForm = initialState.updateLimitacaoConsumoUsuarioForm
        },
        setCreateLimitacaoConsumoUsuarioOpenFormState(state, action: PayloadAction<boolean>) {
            state.createLimitacaoConsumoUsuarioOpenForm = action.payload
        },
        setUpdateLimitacaoConsumoUsuarioOpenFormState(state, action: PayloadAction<boolean>) {
            state.updateLimitacaoConsumoUsuarioOpenForm = action.payload
        },
        setCreateLimitacaoConsumoPerfilFormIdPerfilState(state, action: PayloadAction<number>) {
            state.createLimitacaoConsumoPerfilForm.fields.IdPerfil = action.payload
        },
        setCreateLimitacaoConsumoPerfilFormIdUsuarioState(state, action: PayloadAction<number>) {
            state.createLimitacaoConsumoPerfilForm.fields.IdUsuario = action.payload
        },
        setCreateLimitacaoConsumoPerfilFormIdProdutoState(state, action: PayloadAction<number>) {
            state.createLimitacaoConsumoPerfilForm.fields.IdProduto = action.payload
        },
        setCreateLimitacaoConsumoPerfilFormIdTipoConsumoState(state, action: PayloadAction<number[]>) {
            state.createLimitacaoConsumoPerfilForm.fields.IdTipoConsumo = action.payload
        },
        setCreateLimitacaoConsumoPerfilFormIsGrupoState(state, action: PayloadAction<boolean>) {
            state.createLimitacaoConsumoPerfilForm.fields.IsGrupo = action.payload
        },
        setCreateLimitacaoConsumoPerfilFormQuantidadeState(state, action: PayloadAction<number>) {
            state.createLimitacaoConsumoPerfilForm.fields.Quantidade = action.payload
        },
        resetCreateLimitacaoConsumoPerfilFormState(state) {
            state.createLimitacaoConsumoPerfilForm = initialState.createLimitacaoConsumoPerfilForm
        },
        setCreateLimitacaoConsumoPerfilOpenFormState(state, action: PayloadAction<boolean>) {
            state.createLimitacaoConsumoPerfilOpenForm = action.payload
        },
        setUpdateLimitacaoConsumoPerfilFormIdLimitacaoConsumoPerfilState(state, action: PayloadAction<number>) {
            state.updateLimitacaoConsumoPerfilForm.fields.IdLimitacaoConsumoPerfil = action.payload
        },
        setUpdateLimitacaoConsumoPerfilFormIdPerfilState(state, action: PayloadAction<number>) {
            state.updateLimitacaoConsumoPerfilForm.fields.IdPerfil = action.payload
        },
        setUpdateLimitacaoConsumoPerfilFormQuantidadeState(state, action: PayloadAction<number>) {
            state.updateLimitacaoConsumoPerfilForm.fields.Quantidade = action.payload
        },
        setUpdateLimitacaoConsumoPerfilFormIdGrupoTravaState(state, action: PayloadAction<number | undefined>) {
            state.updateLimitacaoConsumoPerfilForm.fields.IdGrupoTrava = action.payload
        },
        setUpdateLimitacaoConsumoPerfilFormAtivoState(state, action: PayloadAction<boolean>) {
            state.updateLimitacaoConsumoPerfilForm.fields.Ativo = action.payload
        },
        resetUpdateLimitacaoConsumoPerfilFormState(state) {
            state.updateLimitacaoConsumoPerfilForm = initialState.updateLimitacaoConsumoPerfilForm
        },
        setUpdateLimitacaoConsumoPerfilOpenFormState(state, action: PayloadAction<boolean>) {
            state.updateLimitacaoConsumoPerfilOpenForm = action.payload
        },
        setLimitacaoConsumoState(state, action: PayloadAction<ILimitacaoConsumoTable[]>) {
            state.limitacaoConsumoList = action.payload
        },
        resetLimitacaoConsumoState(state) {
            state.limitacaoConsumoList = initialState.limitacaoConsumoList
        },
        setLimitacaoConsumoTotalState(state, action: PayloadAction<number>) {
            state.limitacaoConsumoTotal = action.payload
        },
        resetLimitacaoConsumoTotalState(state) {
            state.limitacaoConsumoTotal = initialState.limitacaoConsumoTotal
        },
        setClienteSelecionadoState(state, action: PayloadAction<ICliente>) {
            state.clienteSelecionado = action.payload
        },
        resetClienteSelecionadoState(state) {
            state.clienteSelecionado = initialState.clienteSelecionado
        },
        setUsuarioSelecionadoState(state, action: PayloadAction<Omit<IUsuario, "Senha">>) {
            state.usuarioSelecionado = action.payload
        },
        resetUsuarioSelecionadoState(state) {
            state.usuarioSelecionado = initialState.usuarioSelecionado
        },
        setPerfilSelecionadoState(state, action: PayloadAction<IPerfil>) {
            state.perfilSelecionado = action.payload
        },
        resetPerfilSelecionadoState(state) {
            state.perfilSelecionado = initialState.perfilSelecionado
        },
        setProdutoLimitacaoConsumoListState(state, action: PayloadAction<IProdutoLimitacaoConsumoDetails[]>) {
            state.produtosList = action.payload
        },
        resetProdutoLimitacaoConsumoListState(state) {
            state.produtosList = initialState.produtosList
        }
    }
})

export const {
    resetLimitacaoConsumoClientesState,
    setLimitacaoConsumoClientesState,
    setLimitacaoConsumoPaginationState,
    resetCreateLimitacaoConsumoClienteFormState,
    resetLimitacaoConsumoPaginationState,
    resetLimitacaoConsumoUsuariosState,
    resetUpdateLimitacaoConsumoClienteFormState,
    setCreateLimitacaoConsumoClienteFormDeliveredState,
    setCreateLimitacaoConsumoClienteFormErrorsState,
    setCreateLimitacaoConsumoClienteFormIdClienteState,
    setCreateLimitacaoConsumoClienteFormIdUsuarioState,
    setCreateLimitacaoConsumoClienteFormIdProdutoState,
    setCreateLimitacaoConsumoClienteFormModoTravaClienteState,
    setCreateLimitacaoConsumoClienteFormIdTipoConsumoState,
    setCreateLimitacaoConsumoClienteFormQuantidadeState,
    setCreateLimitacaoConsumoClienteFormIsGrupoState,
    setCreateLimitacaoConsumoClienteFormTouchedState,
    setLimitacaoConsumoLimitState,
    setLimitacaoConsumoPageState,
    setLimitacaoConsumoUsuariosState,
    setUpdateLimitacaoConsumoClienteFormAtivoState,
    setUpdateLimitacaoConsumoClienteFormDeliveredState,
    setUpdateLimitacaoConsumoClienteFormErrorsState,
    setUpdateLimitacaoConsumoClienteFormIdLimitacaoConsumoClienteState,
    setUpdateLimitacaoConsumoClienteFormQuantidadeState,
    setUpdateLimitacaoConsumoClienteFormTouchedState,
    setUpdateLimitacaoConsumoClienteFormIdGrupoTravaState,
    setUpdateLimitacaoConsumoClienteFormIdClienteState,
    setCreateLimitacaoConsumoClienteOpenFormState,
    setUpdateLimitacaoConsumoClienteOpenFormState,
    resetLimitacaoConsumoState,
    setLimitacaoConsumoState,
    resetLimitacaoConsumoTotalState,
    setLimitacaoConsumoTotalState,
    resetClienteSelecionadoState,
    setClienteSelecionadoState,
    resetCreateLimitacaoConsumoUsuarioFormState,
    resetUpdateLimitacaoConsumoUsuarioFormState,
    resetUsuarioSelecionadoState,
    setCreateLimitacaoConsumoUsuarioFormDeliveredState,
    setCreateLimitacaoConsumoUsuarioFormErrorsState,
    setCreateLimitacaoConsumoUsuarioFormIdProdutoState,
    setCreateLimitacaoConsumoUsuarioFormIdTipoConsumoState,
    setCreateLimitacaoConsumoUsuarioFormIsGrupoState,
    setCreateLimitacaoConsumoUsuarioFormIdUsuarioState,
    setCreateLimitacaoConsumoUsuarioFormQuantidadeState,
    setCreateLimitacaoConsumoUsuarioFormTouchedState,
    setCreateLimitacaoConsumoUsuarioOpenFormState,
    setUpdateLimitacaoConsumoUsuarioFormAtivoState,
    setUpdateLimitacaoConsumoUsuarioFormDeliveredState,
    setUpdateLimitacaoConsumoUsuarioFormErrorsState,
    setUpdateLimitacaoConsumoUsuarioFormIdLimitacaoConsumoUsuarioState,
    setUpdateLimitacaoConsumoUsuarioFormIdUsuarioState,
    setUpdateLimitacaoConsumoUsuarioFormIdGrupoTravaState,
    setUpdateLimitacaoConsumoUsuarioFormQuantidadeState,
    setUpdateLimitacaoConsumoUsuarioFormTouchedState,
    setUpdateLimitacaoConsumoUsuarioOpenFormState,
    setCreateLimitacaoConsumoPerfilFormIdPerfilState,
    setCreateLimitacaoConsumoPerfilFormIdUsuarioState,
    setCreateLimitacaoConsumoPerfilFormIdProdutoState,
    setCreateLimitacaoConsumoPerfilFormIdTipoConsumoState,
    setCreateLimitacaoConsumoPerfilFormIsGrupoState,
    setCreateLimitacaoConsumoPerfilFormQuantidadeState,
    resetCreateLimitacaoConsumoPerfilFormState,
    setCreateLimitacaoConsumoPerfilOpenFormState,
    setUpdateLimitacaoConsumoPerfilFormIdLimitacaoConsumoPerfilState,
    setUpdateLimitacaoConsumoPerfilFormIdPerfilState,
    setUpdateLimitacaoConsumoPerfilFormIdGrupoTravaState,
    setUpdateLimitacaoConsumoPerfilFormQuantidadeState,
    setUpdateLimitacaoConsumoPerfilFormAtivoState,
    resetUpdateLimitacaoConsumoPerfilFormState,
    setUpdateLimitacaoConsumoPerfilOpenFormState,
    setUsuarioSelecionadoState,
    setLimitacaoConsumoPerfisState,
    resetLimitacaoConsumoPerfisState,
    setPerfilSelecionadoState,
    resetPerfilSelecionadoState,
    resetProdutoLimitacaoConsumoListState,
    setProdutoLimitacaoConsumoListState,
    resetCreateLimitacaoConsumoClienteFormErrorsState,
    resetCreateLimitacaoConsumoUsuarioFormErrorsState
} = limitacaoConsumoSlice.actions

export const limitacaoConsumoReducer = limitacaoConsumoSlice.reducer